import { useEffect, useMemo, useRef, useState } from 'react';
import type { ApSummary, Floor, ReferencePoint, StoredFingerprint, StoredReport } from '@wrp/shared';
import * as api from '../api';
import { useDialogs } from '../components/Dialogs';
import { FloorMap } from '../components/FloorMap';
import { Icon, IconButton } from '../components/Icon';
import { RssiBar } from '../components/RssiBar';
import { formatTime } from '../format';
import type { Point } from '../format';
import { clampToFloor, roundPoint } from '../mapUtils';
import { useApp, useSafeAction, useStreamEvent } from '../store';
import { parseNumber, useRecordedReportIds } from './shared';

// bssid === null: place every still-unplaced AP in turn (strongest first).
type Pick = { kind: 'rp' } | { kind: 'ap'; bssid: string | null } | null;

function nextLabel(existing: ReferencePoint[]): string {
  const used = new Set(existing.map((rp) => rp.label.trim().toLowerCase()));
  for (let n = existing.length + 1; ; n++) {
    if (!used.has(String(n))) return String(n);
  }
}

export function SurveyMode({ floor, latest }: { floor: Floor; latest: StoredReport | null }) {
  const {
    referencePoints,
    aps,
    fingerprints,
    fingerprintCountByRefPoint,
    refreshReferencePoints,
    refreshAps,
    refreshFingerprints,
    pushToast,
  } = useApp();
  const dialogs = useDialogs();
  const safe = useSafeAction();
  const recorded = useRecordedReportIds();

  const [pick, setPick] = useState<Pick>(null);
  const [selectedRpId, setSelectedRpId] = useState<string | null>(null);
  const [selectedBssid, setSelectedBssid] = useState<string | null>(null);
  // The stored scan behind a point's fingerprint, shown in place of the live one.
  const [detail, setDetail] = useState<{ rpId: string; report: StoredReport } | null>(null);

  const floorId = floor.id;
  useEffect(() => {
    setPick(null);
    setSelectedRpId(null);
    setSelectedBssid(null);
    setDetail(null);
  }, [floorId]);

  useEffect(() => {
    if (!pick) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPick(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pick]);

  const floorPoints = useMemo(
    () => referencePoints.filter((rp) => rp.floor_id === floorId).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
    [referencePoints, floorId],
  );
  const completed = floorPoints.filter((rp) => (fingerprintCountByRefPoint[rp.id] ?? 0) > 0).length;
  const selectedRp = floorPoints.find((rp) => rp.id === selectedRpId) ?? null;

  const sortedAps = useMemo(
    () => [...aps].sort((a, b) => Number(b.x !== null) - Number(a.x !== null) || b.avg_rssi - a.avg_rssi),
    [aps],
  );
  const unplacedAps = useMemo(() => sortedAps.filter((a) => a.x === null), [sortedAps]);
  const apTarget =
    pick?.kind === 'ap' ? (pick.bssid ? (aps.find((a) => a.bssid === pick.bssid) ?? null) : (unplacedAps[0] ?? null)) : null;
  const placingRef = useRef(false);

  // Auto-placing ends by itself once every AP has a position.
  useEffect(() => {
    if (pick?.kind === 'ap' && pick.bssid === null && unplacedAps.length === 0) setPick(null);
  }, [pick, unplacedAps.length]);

  // ------------------------------------------------------- reference points
  const addReferencePoint = async (point: Point) => {
    const p = roundPoint(clampToFloor(floor, point));
    const values = await dialogs.form('Add reference point', [
      { key: 'label', label: 'Label', value: nextLabel(floorPoints), placeholder: '1' },
      { key: 'x', label: 'X (m)', type: 'number', step: '0.01', value: String(p.x) },
      { key: 'y', label: 'Y (m)', type: 'number', step: '0.01', value: String(p.y) },
    ]);
    if (!values) return;
    const x = parseNumber(values.x);
    const y = parseNumber(values.y);
    const label = values.label?.trim();
    if (!label || x === null || y === null) {
      pushToast('error', 'A label and numeric X / Y are required');
      return;
    }
    const created = await safe('Failed to add reference point', () =>
      api.createReferencePoint({ floor_id: floorId, label, x, y }),
    );
    if (created) {
      setSelectedRpId(created.id);
      await refreshReferencePoints();
    }
  };

  const editReferencePoint = async (rp: ReferencePoint) => {
    const values = await dialogs.form(`Edit ${rp.label}`, [
      { key: 'label', label: 'Label', value: rp.label },
      { key: 'x', label: 'X (m)', type: 'number', step: '0.01', value: String(rp.x) },
      { key: 'y', label: 'Y (m)', type: 'number', step: '0.01', value: String(rp.y) },
    ]);
    if (!values) return;
    const x = parseNumber(values.x);
    const y = parseNumber(values.y);
    const label = values.label?.trim();
    if (!label || x === null || y === null) {
      pushToast('error', 'A label and numeric X / Y are required');
      return;
    }
    const updated = await safe('Failed to update reference point', () =>
      api.updateReferencePoint(rp.id, { label, x, y }),
    );
    if (updated) await Promise.all([refreshReferencePoints(), refreshFingerprints()]);
  };

  const deleteReferencePoint = async (rp: ReferencePoint) => {
    const count = fingerprintCountByRefPoint[rp.id] ?? 0;
    const ok = await dialogs.confirm(
      count > 0
        ? `Delete ${rp.label}? Its ${count} fingerprint(s) stay in the database but lose the link to this point.`
        : `Delete ${rp.label}?`,
    );
    if (!ok) return;
    await safe('Failed to delete reference point', async () => {
      await api.deleteReferencePoint(rp.id);
      if (selectedRpId === rp.id) setSelectedRpId(null);
      await Promise.all([refreshReferencePoints(), refreshFingerprints()]);
    });
  };

  const deleteAllReferencePoints = async () => {
    const withData = floorPoints.filter((rp) => (fingerprintCountByRefPoint[rp.id] ?? 0) > 0).length;
    const ok = await dialogs.confirm(
      `Delete all ${floorPoints.length} reference points of this floor?` +
        (withData > 0 ? ` ${withData} of them are completed; their fingerprints stay in the database but lose the link.` : ''),
    );
    if (!ok) return;
    await safe('Failed to delete reference points', async () => {
      for (const rp of floorPoints) await api.deleteReferencePoint(rp.id);
    });
    setSelectedRpId(null);
    await Promise.all([refreshReferencePoints(), refreshFingerprints()]);
  };

  const moveReferencePoint = async (rp: ReferencePoint, point: Point) => {
    await safe('Failed to move reference point', () => api.updateReferencePoint(rp.id, point));
    await Promise.all([refreshReferencePoints(), refreshFingerprints()]);
  };

  const captureScan = async (rp: ReferencePoint) => {
    if (!latest) return;
    const fp = await safe('Failed to record fingerprint', () =>
      api.createFingerprint(latest.id, { reference_point_id: rp.id }),
    );
    if (fp) {
      pushToast('success', `Scan recorded at ${rp.label}`);
      await refreshFingerprints();
    }
  };

  /**
   * Show the scan that was recorded at a point. A labeled report supersedes the previous one at
   * the same point, so there is normally exactly one; the newest wins if a scan was also recorded
   * by hand.
   */
  const showRecordedScan = async (rp: ReferencePoint) => {
    setSelectedRpId(rp.id);
    setSelectedBssid(null);
    const fp = fingerprints
      .filter((f) => f.reference_point_id === rp.id)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    if (!fp) return;
    const report = await safe('Failed to load the recorded scan', () => api.getReport(fp.report_id));
    if (report) setDetail({ rpId: rp.id, report });
  };

  // Re-surveying the point on show replaces its report, so follow the new measurement.
  useStreamEvent<StoredFingerprint>('fingerprint', (fp) => {
    if (!detail || fp.reference_point_id !== detail.rpId || fp.report_id === detail.report.id) return;
    const rpId = detail.rpId;
    void safe('Failed to load the recorded scan', () => api.getReport(fp.report_id)).then((report) => {
      if (report) setDetail({ rpId, report });
    });
  });

  // ------------------------------------------------------------------- APs
  const placeAp = async (ap: ApSummary, point: Point) => {
    const p = roundPoint(clampToFloor(floor, point));
    const updated = await safe('Failed to place AP', () => api.updateApPosition(ap.bssid, { floor_id: floorId, ...p }));
    if (updated) await refreshAps();
  };

  const editAp = async (ap: ApSummary) => {
    const values = await dialogs.form(`Position of ${ap.ssid ?? ap.bssid}`, [
      { key: 'x', label: 'X (m)', type: 'number', step: '0.01', value: ap.x !== null ? String(ap.x) : '' },
      { key: 'y', label: 'Y (m)', type: 'number', step: '0.01', value: ap.y !== null ? String(ap.y) : '' },
    ]);
    if (!values) return;
    const x = parseNumber(values.x);
    const y = parseNumber(values.y);
    if (x === null || y === null) {
      pushToast('error', 'Numeric X / Y are required');
      return;
    }
    await placeAp(ap, { x, y });
  };

  const unplaceAp = async (ap: ApSummary) => {
    const ok = await dialogs.confirm(`Remove ${ap.ssid ?? ap.bssid} from the map?`);
    if (!ok) return;
    await safe('Failed to remove AP', () => api.deleteApPosition(ap.bssid));
    if (selectedBssid === ap.bssid) setSelectedBssid(null);
    await refreshAps();
  };

  // ------------------------------------------------------------------- map
  const handleMapClick = (point: Point) => {
    if (pick?.kind === 'rp') {
      void addReferencePoint(point);
    } else if (pick?.kind === 'ap') {
      if (!apTarget || placingRef.current) return;
      const single = pick.bssid !== null;
      placingRef.current = true;
      void placeAp(apTarget, point).finally(() => {
        placingRef.current = false;
        if (single) setPick(null);
      });
    } else {
      setSelectedRpId(null);
      setSelectedBssid(null);
    }
  };

  const toggleAddReferencePoints = () => setPick(pick?.kind === 'rp' ? null : { kind: 'rp' });
  const toggleAutoPlaceAps = () => {
    if (pick?.kind === 'ap' && pick.bssid === null) {
      setPick(null);
    } else if (unplacedAps.length === 0) {
      pushToast('info', aps.length === 0 ? 'No APs seen yet' : 'Every AP is already placed');
    } else {
      setPick({ kind: 'ap', bssid: null });
    }
  };

  const apName = apTarget ? (apTarget.ssid ?? apTarget.bssid) : '';
  const hint =
    pick?.kind === 'rp'
      ? 'Click the plan to add a reference point · Esc to stop'
      : pick?.kind === 'ap' && apTarget
        ? pick.bssid === null
          ? `Click the plan to place ${apName} · ${unplacedAps.length} left · Esc to stop`
          : `Click the plan to place ${apName} · Esc to cancel`
        : null;

  const scanRecorded = latest ? recorded.has(latest.id) : false;
  // The panel follows the selection: a point's recorded scan while one is open for it, the live
  // scan otherwise. Selecting another point drops back to live on its own.
  const shownReport = detail && detail.rpId === selectedRpId ? detail.report : null;
  const panelReport = shownReport ?? latest;
  const scanAps = panelReport?.aps ?? [];

  return (
    <>
      <div className="live__map">
        <FloorMap
          floor={floor}
          referencePoints={floorPoints}
          aps={aps.filter((ap) => ap.floor_id === floorId)}
          fingerprintCountByRefPoint={fingerprintCountByRefPoint}
          selectedReferencePointId={selectedRpId}
          selectedApBssid={selectedBssid}
          draggableReferencePoints
          picking={pick !== null}
          hint={hint}
          tools={
            <>
              <IconButton
                icon="plus"
                label={pick?.kind === 'rp' ? 'Stop adding reference points' : 'Add reference points on the map'}
                active={pick?.kind === 'rp'}
                size={15}
                onClick={toggleAddReferencePoints}
              />
              <IconButton
                icon="wifi"
                label={
                  pick?.kind === 'ap' && pick.bssid === null
                    ? 'Stop placing access points'
                    : `Place access points on the map (${unplacedAps.length} not placed)`
                }
                active={pick?.kind === 'ap' && pick.bssid === null}
                size={15}
                onClick={toggleAutoPlaceAps}
              />
            </>
          }
          onMapClick={handleMapClick}
          onReferencePointClick={(rp) => {
            setSelectedRpId(rp.id);
            setSelectedBssid(null);
          }}
          onApClick={(ap) => {
            setSelectedBssid(ap.bssid);
            setSelectedRpId(null);
          }}
          onReferencePointMove={(rp, point) => void moveReferencePoint(rp, point)}
        />
      </div>

      <aside className="live__side">
        <section className="box">
          <header className="box__head">
            <Icon name="pin" />
            <h3>Reference points</h3>
            <span className="box__count">
              {completed}/{floorPoints.length}
            </span>
            <span className="box__spacer" />
            <IconButton
              icon="broom"
              label="Delete all reference points of this floor"
              size={14}
              danger
              disabled={floorPoints.length === 0}
              onClick={() => void deleteAllReferencePoints()}
            />
          </header>
          <div className="progress" title={`${completed} of ${floorPoints.length} completed`}>
            <div
              className="progress__fill"
              style={{ width: floorPoints.length ? `${(completed / floorPoints.length) * 100}%` : '0%' }}
            />
          </div>
          <ul className="box__list">
            {floorPoints.length === 0 ? <li className="box__empty">No reference points yet. Use the + tool on the map.</li> : null}
            {floorPoints.map((rp) => {
              const count = fingerprintCountByRefPoint[rp.id] ?? 0;
              return (
                <li
                  key={rp.id}
                  className={`row${rp.id === selectedRpId ? ' is-selected' : ''}`}
                  onClick={() => {
                    setSelectedRpId(rp.id);
                    setSelectedBssid(null);
                  }}
                >
                  <span className={`row__status ${count > 0 ? 'is-done' : 'is-pending'}`} title={count > 0 ? 'Completed' : 'Pending'}>
                    <Icon name={count > 0 ? 'check' : 'circle'} size={13} />
                  </span>
                  <span className="row__main">
                    <strong>{rp.label}</strong>
                    <span className="muted">
                      {rp.x.toFixed(2)}, {rp.y.toFixed(2)} m
                    </span>
                  </span>
                  <span className="row__actions" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      icon="list"
                      label={count > 0 ? `Show the scan recorded at ${rp.label}` : 'Nothing recorded here yet'}
                      active={shownReport !== null && selectedRpId === rp.id}
                      disabled={count === 0}
                      size={14}
                      onClick={() => void showRecordedScan(rp)}
                    />
                    <IconButton icon="edit" label={`Edit ${rp.label}`} size={14} onClick={() => void editReferencePoint(rp)} />
                    <IconButton icon="trash" label={`Delete ${rp.label}`} danger size={14} onClick={() => void deleteReferencePoint(rp)} />
                  </span>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="box">
          <header className="box__head">
            <Icon name="wifi" />
            <h3>Access points</h3>
            <span className="box__count">
              {aps.filter((ap) => ap.x !== null && ap.floor_id === floorId).length}/{aps.length} placed
            </span>
          </header>
          <ul className="box__list">
            {sortedAps.length === 0 ? <li className="box__empty">No APs seen yet. Send a scan from the device.</li> : null}
            {sortedAps.map((ap) => {
              const placed = ap.x !== null && ap.y !== null;
              const name = ap.ssid ?? ap.bssid;
              return (
                <li
                  key={ap.bssid}
                  className={`row${ap.bssid === selectedBssid ? ' is-selected' : ''}`}
                  onClick={() => {
                    setSelectedBssid(ap.bssid);
                    setSelectedRpId(null);
                  }}
                >
                  <span className={`row__status ${placed ? 'is-done' : 'is-pending'}`} title={placed ? 'Placed' : 'Not placed'}>
                    <Icon name={placed ? 'check' : 'circle'} size={13} />
                  </span>
                  <span className="row__main">
                    <strong>{name}</strong>
                    <span className="muted">
                      {placed ? `${ap.x?.toFixed(1)}, ${ap.y?.toFixed(1)} m` : ap.bssid} · avg {ap.avg_rssi} dBm
                    </span>
                  </span>
                  <span className="row__actions" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      icon="pin"
                      label={pick?.kind === 'ap' && pick.bssid === ap.bssid ? 'Cancel placing' : `Place ${name} on the map`}
                      active={pick?.kind === 'ap' && pick.bssid === ap.bssid}
                      size={14}
                      onClick={() => setPick(pick?.kind === 'ap' && pick.bssid === ap.bssid ? null : { kind: 'ap', bssid: ap.bssid })}
                    />
                    <IconButton icon="edit" label={`Set coordinates of ${name}`} size={14} onClick={() => void editAp(ap)} />
                    <IconButton icon="trash" label={`Remove ${name} from the map`} danger disabled={!placed} size={14} onClick={() => void unplaceAp(ap)} />
                  </span>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="box">
          <header className="box__head">
            <Icon name="radar" />
            <h3>Scan detail</h3>
            <span className="box__count">
              {!panelReport
                ? 'waiting'
                : `${shownReport ? `recorded at ${selectedRp?.label}` : 'live'} · ${scanAps.length} APs · ` +
                  formatTime(panelReport.received_at)}
            </span>
            <span className="box__spacer" />
            {shownReport ? (
              <IconButton icon="close" label="Back to the live scan" onClick={() => setDetail(null)} />
            ) : (
              <IconButton
                icon="camera"
                label={
                  !latest
                    ? 'No scan received yet'
                    : !selectedRp
                      ? 'Select a reference point to record this scan'
                      : scanRecorded
                        ? 'This scan is already recorded'
                        : `Record this scan at ${selectedRp.label}`
                }
                primary
                disabled={!latest || !selectedRp || scanRecorded}
                onClick={() => selectedRp && void captureScan(selectedRp)}
              />
            )}
          </header>
          <ul className="box__list">
            {!panelReport ? <li className="box__empty">Waiting for a scan report…</li> : null}
            {scanAps.map((ap) => (
              <li key={ap.bssid} className="row row--scan">
                <span className="row__main">
                  <strong>{ap.ssid || ap.bssid}</strong>
                  <span className="muted">{ap.bssid}{ap.channel ? ` · ch ${ap.channel}` : ''}</span>
                </span>
                {typeof ap.signal_strength === 'number' ? <RssiBar dbm={ap.signal_strength} /> : null}
              </li>
            ))}
          </ul>
        </section>
      </aside>
    </>
  );
}
